/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/**
 * Configures anomaly groups for the overview pages, including requested KPI
 * sensors, aggregations, navigation metadata, and API calculation adapters.
 */

import { type LucideIcon } from 'lucide-react';

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';
import {
    BASE_LOAD_COST_DAY_STATUS_DEFINITION,
    HEATING_ENERGY_WEEKEND_REDUCTION_STATUS_DEFINITION,
    PV_DTC_STATUS_DEFINITION,
    WATER_LOSS_DAILY_DTC_STATUS_DEFINITION,
    WATER_LOSS_DTC_STATUS_DEFINITION,
} from '@/config/kpi/statusConfig';
import {
    getBaseLoadStatus,
    getHeatingEnergyStatus,
    getPvPredictionStatus,
    getWaterLossStatus,
} from '@/config/kpi/statusFunctions';

/* Import components */
import {
    ElectricityIcon,
    HeatIcon,
    PhotovoltaicsIcon,
    WaterIcon,
} from '@/components/icon/SupplyIcon';

/* Import types */
import type { SystemDashboardKpiStatusDefinition } from '@/lib/overview/systemDashboardKpiStatus';
import type { TimeSeriesCalc } from '@/lib/timeSeries/timeSeriesValue';
import type { DeviceSensorGroup, DeviceSensorMetadataGroup } from '@/types/deviceSensor';

export interface OverviewAnomalyKpiConfig {
    conversion: number;
    fractionDigits: number;
    sensorGroup?: DeviceSensorGroup;
    sensorType: string;
    systemAggregation: 'average' | 'max' | 'sum';
    unit: string | null;
}

export interface OverviewAnomalyConfig {
    badgeLabel: string;
    calc: TimeSeriesCalc;
    description: string;
    group: DeviceSensorMetadataGroup;
    icon: LucideIcon;
    key: string;
    kpiGridClassName: string;
    kpis: OverviewAnomalyKpiConfig[];
    label: string;
    route: string;
    statusDefinition: SystemDashboardKpiStatusDefinition;
    statusLabels: Record<number, string>;
}

/* API calculation adapters for the system overview dataset. */
const BASE_LOAD_OVERVIEW_CALC: TimeSeriesCalc = ({ values }) => ({
    status: getBaseLoadStatus(values.CostDay),
});

const PV_PREDICTION_OVERVIEW_CALC: TimeSeriesCalc = ({ values }) => ({
    status: getPvPredictionStatus(values.dtc),
});

const HEATING_ENERGY_OVERVIEW_CALC: TimeSeriesCalc = ({ values }) => ({
    status: getHeatingEnergyStatus(values.WeekendReductionPercent),
});

const WATER_LOSS_SYSTEM_DASHBOARD_CALC: TimeSeriesCalc = ({ values }) => ({
    status: getWaterLossStatus(values.dtc_7d),
});

export const BASE_LOAD_OVERVIEW_CONFIG: OverviewAnomalyConfig = {
    badgeLabel: 'bl',
    calc: BASE_LOAD_OVERVIEW_CALC,
    description: 'Overview of all available base load metrics.',
    group: 'BL',
    icon: ElectricityIcon,
    key: 'base-load',
    kpiGridClassName: 'grid-cols-2',
    kpis: [
        {
            conversion: 1,
            fractionDigits: 2,
            sensorType: 'CostDay',
            systemAggregation: 'sum',
            unit: '€',
        },
        {
            conversion: 1,
            fractionDigits: 2,
            sensorType: 'Min',
            systemAggregation: 'average',
            unit: 'kW',
        },
        {
            conversion: 1,
            fractionDigits: 0,
            sensorType: 'Severity',
            systemAggregation: 'max',
            unit: null,
        },
    ],
    label: 'Base Load',
    route: APP_ROUTES.baseload,
    statusDefinition: BASE_LOAD_COST_DAY_STATUS_DEFINITION,
    statusLabels: {
        [-1]: 'Implausible',
        0: 'OK',
        1: 'Warning',
        2: 'Error',
    },
};

export const PV_PREDICTION_OVERVIEW_CONFIG: OverviewAnomalyConfig = {
    badgeLabel: 'pv',
    calc: PV_PREDICTION_OVERVIEW_CALC,
    description: 'Overview of all available PV prediction metrics.',
    group: 'PV',
    icon: PhotovoltaicsIcon,
    key: 'pv-prediction',
    kpiGridClassName: 'grid-cols-3',
    kpis: [
        {
            conversion: 1,
            fractionDigits: 2,
            sensorType: 'ratio_30',
            systemAggregation: 'average',
            unit: null,
        },
        {
            conversion: 1,
            fractionDigits: 0,
            sensorType: 'dtc',
            systemAggregation: 'max',
            unit: null,
        },
        {
            conversion: 0.001,
            fractionDigits: 2,
            sensorGroup: null,
            sensorType: 'W_1d',
            systemAggregation: 'sum',
            unit: 'MWh',
        },
    ],
    label: 'PV Prediction',
    route: APP_ROUTES.pvprediction,
    statusDefinition: PV_DTC_STATUS_DEFINITION,
    statusLabels: {
        [-1]: 'Implausible',
        0: 'OK',
        1: 'Warning',
        2: 'Error',
    },
};

export const HEATING_ENERGY_OVERVIEW_CONFIG: OverviewAnomalyConfig = {
    badgeLabel: 'heating_energy',
    calc: HEATING_ENERGY_OVERVIEW_CALC,
    description: 'Overview of all available heating energy metrics.',
    group: 'HE',
    icon: HeatIcon,
    key: 'heating-energy',
    kpiGridClassName: 'grid-cols-2',
    kpis: [
        {
            conversion: 1,
            fractionDigits: 1,
            sensorType: 'CutoffTemperature',
            systemAggregation: 'average',
            unit: '°C',
        },
        {
            conversion: 1,
            fractionDigits: 2,
            sensorType: 'WeekendReductionPercent',
            systemAggregation: 'average',
            unit: '%',
        },
        {
            conversion: 1,
            fractionDigits: 2,
            sensorType: 'WeekendReductionValue',
            systemAggregation: 'sum',
            unit: 'kWh',
        },
        {
            conversion: 1 / 1000,
            fractionDigits: 1,
            sensorType: 'SavedEnergy',
            systemAggregation: 'sum',
            unit: 'MWh',
        },
    ],
    label: 'Heating Energy',
    route: APP_ROUTES.heatingenergy,
    statusDefinition: HEATING_ENERGY_WEEKEND_REDUCTION_STATUS_DEFINITION,
    statusLabels: {
        [-1]: 'Implausible',
        0: 'OK',
        1: 'Warning',
        2: 'Error',
    },
};

/** Configures water-loss metrics for System Dashboard and anomaly detail pages. */
export const WATER_LOSS_SYSTEM_DASHBOARD_CONFIG: OverviewAnomalyConfig = {
    badgeLabel: 'water_bl',
    calc: WATER_LOSS_SYSTEM_DASHBOARD_CALC,
    description: 'Water loss over the last 7 days in m³/h',
    group: 'WL',
    icon: WaterIcon,
    key: 'water-loss',
    kpiGridClassName: 'grid-cols-1',
    kpis: [
        {
            conversion: 1,
            fractionDigits: 2,
            sensorType: 'qv_loss_7d',
            systemAggregation: 'sum',
            unit: 'm³/h',
        },
        {
            conversion: 1,
            fractionDigits: 0,
            sensorType: 'dtc_7d',
            systemAggregation: 'max',
            unit: null,
        },
    ],
    label: 'Water loss last 7 days',
    route: APP_ROUTES.waterloss,
    statusDefinition: WATER_LOSS_DTC_STATUS_DEFINITION,
    statusLabels: {
        [-1]: 'Implausible',
        0: 'OK',
        1: 'Warning',
        2: 'Error',
    },
};

/** Requests only the daily water status needed by the cards on /home and /systems. */
export const WATER_LOSS_SYSTEM_OVERVIEW_CONFIG: OverviewAnomalyConfig = {
    ...WATER_LOSS_SYSTEM_DASHBOARD_CONFIG,
    calc: WATER_LOSS_DAILY_DTC_STATUS_DEFINITION.calc,
    description: 'Water loss status over the last day',
    kpis: [{
        conversion: 1,
        fractionDigits: 0,
        sensorType: 'dtc',
        systemAggregation: 'max',
        unit: null,
    }],
    label: 'Water loss last day',
    statusDefinition: WATER_LOSS_DAILY_DTC_STATUS_DEFINITION,
};

export const BASE_LOAD_OVERVIEW_CONFIGS = [BASE_LOAD_OVERVIEW_CONFIG];
export const PV_PREDICTION_OVERVIEW_CONFIGS = [PV_PREDICTION_OVERVIEW_CONFIG];
export const HEATING_ENERGY_OVERVIEW_CONFIGS = [HEATING_ENERGY_OVERVIEW_CONFIG];
export const WATER_LOSS_OVERVIEW_CONFIGS = [WATER_LOSS_SYSTEM_DASHBOARD_CONFIG];

export const ANOMALY_NAVIGATION_CONFIGS = [
    PV_PREDICTION_OVERVIEW_CONFIG,
    BASE_LOAD_OVERVIEW_CONFIG,
    HEATING_ENERGY_OVERVIEW_CONFIG,
    WATER_LOSS_SYSTEM_DASHBOARD_CONFIG,
];

export const OVERVIEW_ANOMALY_CONFIGS = [
    BASE_LOAD_OVERVIEW_CONFIG,
    PV_PREDICTION_OVERVIEW_CONFIG,
    HEATING_ENERGY_OVERVIEW_CONFIG,
];

/** Adds daily water status to the system overview cards. */
export const SYSTEM_OVERVIEW_ANOMALY_CONFIGS = [
    ...OVERVIEW_ANOMALY_CONFIGS,
    WATER_LOSS_SYSTEM_OVERVIEW_CONFIG,
];
